import { useEffect, useRef, useState, useMemo } from "react";
import { Reactable } from "../../components/ui/reactable";
import { EMPLOYEE_ATTENDANCE_DAILY, EMPLOYEE_MISSING_ATTENDANCE_CORRECTION, MONTHWISE_ATTENDANCE_LOG } from "../../constants/api";
import { ContainerCard, EmployeeDetails, ModuleTitleCard } from "../../components/ui/cards";
import { useLocation, useNavigate } from "react-router-dom";
import { Calendar, ChartLine, History } from "lucide-react";
import { Button } from "../../components/ui/button";
import dayjs from "dayjs";
import { DialogModal } from "../../components/ui/dialogModal";
import AttendanceEdit from "./AttendanceEdit";
import AttendanceCorrectionHistory from "./AttendanceCorrectionHistory";
import useApiMethods from "../../lib/useApiMethods";
import ToastMsg from "../../components/common/ToastMsg";
import { Textarea } from "../../components/ui/input";
import { cn } from "../../utils/tailwindUtils";
import { modalDescriptionFieldProps, modalFieldLabelClass, modalInputClass, modalTextareaClass } from "../../components/ui/fieldStyles";
import { BranchCell } from "../../components/common/BranchColumn";
import { canReviewShiftChangeRequests } from "./helpers/shiftChangeUtils";

const AttendanceLog = () => {
    const { fetch, create } = useApiMethods()
    const [AttendanceLogData, setAttendanceLogData] = useState([]);
    const [totalRecords, setTotalRecords] = useState(0);
    const [dynamicColumns, setDynamicColumns] = useState([]);
    const [fetchFilter, setFetchFilter] = useState({})
    const [loading, setLoading] = useState(false);
    const [serverTrigger, setServerTrigger] = useState(false);
    const [selectedAttendanceData, setSelectedAttendanceData] = useState(null);
    const [adminEditDialogOpen, setAdminEditDialogOpen] = useState(false);
    const [attendanceRequestDialogOpen, setAttendanceRequestDialogOpen] = useState(false);
    const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
    const [historyEmployeeId, setHistoryEmployeeId] = useState(null);
    const [historyEmployeeName, setHistoryEmployeeName] = useState("");

    // request reason state
    const [reason, setReason] = useState();

    const attendanceEditRef = useRef()

    let navigate = useNavigate()
    let isAdmin = JSON.parse(sessionStorage.getItem('is_admin'))
    let isSuperAdmin = JSON.parse(sessionStorage.getItem('is_super_admin'))

    let location = useLocation()

    const { from, payrollPeriod } = location.state || {}

    // Convert payrollPeriod (number) to proper date objects
    const [selectedMonth, setSelectedMonth] = useState(() => {
        if (payrollPeriod) {
            const currentYear = dayjs().year();
            return dayjs().year(currentYear).month(payrollPeriod - 1);
        }
        return dayjs(); // Current month
    });

    const handleClearLocationState = () => {
        // Clear the navigation state
        navigate(location.pathname, { replace: true, state: {} });

        // Reset to current month
        setSelectedMonth(dayjs());

        // Reset fetch filter to current month
        setFetchFilter({
            month: dayjs().month() + 1,
            year: dayjs().year()
        });

        // Trigger a server refresh
        setServerTrigger(prev => !prev);
    }

    // Add this useEffect to set initial fetchFilter when selectedMonth changes
    useEffect(() => {
        if (selectedMonth) {
            setFetchFilter({
                month: selectedMonth.month() + 1,
                year: selectedMonth.year()
            });
        }
    }, [selectedMonth]); // Remove payrollPeriod from dependencies

    // Add this effect to handle initial load from navigation
    useEffect(() => {
        if (payrollPeriod) {
            const currentYear = dayjs().year();
            setSelectedMonth(dayjs().year(currentYear).month(payrollPeriod - 1));
        }
    }, [payrollPeriod]);

    const statusConfig = {
        "present": { label: "P", bgColor: "#28A745" },
        "absent": { label: "A", bgColor: "#A72828" },
        // Transferred-day labels (a temporary transfer, worked at another branch).
        // Backend sends these via `display_status`; stored status stays present/absent.
        "TP": { label: "TP", bgColor: "#1E7B34" },
        "TA": { label: "TA", bgColor: "#7B1E1E" },
        "holiday": { label: "HD", bgColor: "#EEBA35" },
        "on-duty": { label: "OD", bgColor: "#3595EE" },
        "week-off": { label: "WO", bgColor: "#F9827A" },
        "compensation": { label: "Comp", bgColor: "#FEB8C3" },
        "WFH": { label: "WFH", bgColor: "#8DD6F5" },
        "leave": { label: "L", bgColor: "#CCA6FC" },
        "N/A": { label: "N/A", bgColor: "#b4b1b1" },
    };

    const openCorrectionHistory = (employeeDetails) => {
        const employeeDbId = employeeDetails?.id;
        if (!employeeDbId) {
            ToastMsg("error", "Employee id not found for correction history");
            return;
        }
        setHistoryEmployeeId(employeeDbId);
        setHistoryEmployeeName(employeeDetails?.employee_name || "Employee");
        setHistoryDialogOpen(true);
    };

    const historyFromDate = selectedMonth ? selectedMonth.startOf("month").format("YYYY-MM-DD") : undefined;
    const historyToDate = selectedMonth ? selectedMonth.endOf("month").format("YYYY-MM-DD") : undefined;

    // fetch indiviual attendance data of the particular date based on employee id and date
    const fetchDayWiseAttendanceData = async (employee_id, date) => {
        try {
            const response = await fetch(EMPLOYEE_ATTENDANCE_DAILY, { employee_id: employee_id, date: date });
            if (response.status === "success") {
                if (isAdmin || isSuperAdmin) {
                    setSelectedAttendanceData(response.data.result[0]);
                    setAdminEditDialogOpen(true);
                } else {
                    setSelectedAttendanceData(response.data.result[0]);
                    setAttendanceRequestDialogOpen(true);
                }
            }
        } catch (error) {
            ToastMsg('error', error.message)
        }
    }

    useEffect(() => {
        if (!fetchFilter?.month || !fetchFilter?.year) return;

        const parsedDate = dayjs(`${fetchFilter.year}-${fetchFilter.month}`, "YYYY-M");
        const daysInMonth = parsedDate.daysInMonth();
        const newColumns = [];

        for (let day = 1; day <= daysInMonth; day++) {
            const columnDate = parsedDate.date(day).format("YYYY-MM-DD");

            const columnConfig = {
                accessor: columnDate,
                Header: day.toString(),
                align: "center",
                Cell: ({ row }) => {
                    const record = row ?? row;
                    const attendanceData = record?.attendance_dict?.find(
                        (item) => item.date === columnDate
                    );
                    const isDuty = attendanceData ? attendanceData.is_short_duty : false;
                    // Prefer display_status (carries TP/TA for transferred days); fall
                    // back to the raw status for older responses / normal days.
                    const status = attendanceData ? (attendanceData.display_status ?? attendanceData.status) : "N/A";
                    const statusInfo = statusConfig[status] || statusConfig["N/A"];
                    const noStatus = (attendanceData ? attendanceData.status : null) === null

                    return (
                        <>
                            <Button
                                variant="ghost"
                                className="h-10 w-10 p-0"
                                disabled={noStatus || !attendanceData}
                                onClick={() => {
                                    fetchDayWiseAttendanceData(record?.employee_details?.id, attendanceData?.date);
                                }}
                            >
                                <p className="musterStatusTxt text-xs" style={{ color: statusInfo.bgColor }}>
                                    {isDuty ? 'SD' : statusInfo?.label}
                                </p>
                            </Button>
                        </>
                    );
                },
            };

            newColumns.push(columnConfig);
        }

        setDynamicColumns(newColumns);
    }, [fetchFilter?.month, fetchFilter?.year, isAdmin, isSuperAdmin]);

    // handle save for edit dialog
    const handleSave = async () => {
        try {
            setLoading(true);
            // You'll need to pass a ref or callback to get form data from AttendanceEdit
            if (attendanceEditRef.current) {
                await attendanceEditRef.current.handleSubmit();
            }
            setAdminEditDialogOpen(false);
            setServerTrigger(prev => !prev) // Trigger data refresh
        } catch (error) {
            console.error("Save failed:", error);
        } finally {
            setLoading(false);
        }
    };

    const handleRequest = async () => {
        console.log(selectedAttendanceData);

        const isNightShift = selectedAttendanceData?.shiftDetails?.shift_timings_to < selectedAttendanceData?.shiftDetails?.shift_timings_from;

        const checkOutDate = isNightShift
            ? new Date(new Date(selectedAttendanceData?.date).getTime() + 24 * 60 * 60 * 1000).toISOString().split('T')[0]
            : selectedAttendanceData?.date;

        const payload = {
            date: selectedAttendanceData?.date,
            reason: reason,
            requested_check_in: selectedAttendanceData?.date + ' ' + selectedAttendanceData?.shiftDetails?.shift_timings_from,
            requested_check_out: checkOutDate + ' ' + selectedAttendanceData?.shiftDetails?.shift_timings_to,
            total_shift_hrs: selectedAttendanceData?.shiftDetails?.total_shift_hrs
        };

        try {
            setLoading(true);
            const response = await create(EMPLOYEE_MISSING_ATTENDANCE_CORRECTION, payload)

            if (response?.status === "success") {
                ToastMsg("success", response.message || "Correction request submitted successfully.");
                setAttendanceRequestDialogOpen(false)
                // clear the state
                setSelectedAttendanceData({})
                setReason('')
            } else {
                ToastMsg("error", response.message)
            }

        } catch (error) {
            ToastMsg('error', error.message);
        }
    }

    // handle dialogs hide
    const handleAdminDialogHide = () => {
        setAdminEditDialogOpen(false);
        setSelectedAttendanceData(null);
    };

    const handleRequestDialogHide = () => {
        setAttendanceRequestDialogOpen(false);
        setSelectedAttendanceData(null);
    };

    const AttendanceLogColumns = useMemo(() => [
        {
            accessor: "employee_name",
            Header: "Employee",
            sortable: true,
            fixed: true,
            filterable: true,
            maxWidth: "300px",
            Cell: ({ value, row }) => {
                const record = row?.original ?? row;

                let empDetails = {
                    ...record?.employee_details,
                    designation_id: record?.employee_details?.designation
                        ? {
                            id: record?.employee_details.designation.id,
                            designation_name: record?.employee_details.designation.name,
                        }
                        : { id: 0, designation_name: "" },
                    first_name: record?.employee_details?.employee_name || "-",
                    id: record?.employee_details?.employee_id || "-",
                };

                return (
                    <div className="min-w-0">
                        <EmployeeDetails details={empDetails} size="xs" />
                        <p className="text-[10px] text-gray-500 dark:text-gray-400 truncate ml-12 mt-0.5">
                            <BranchCell record={record} />
                        </p>
                    </div>
                );
            },
        },
        ...((isAdmin || isSuperAdmin) ? [
            {
                accessor: "attendance_chart",
                Header: "Chart",
                align: "center",
                maxWidth: "90px",
                Cell: ({ row }) => {
                    const record = row?.original ?? row;

                    const navigateToAttendanceChart = () => {
                        navigate("/attendance/attendancelogChart", {
                            state: {
                                employeeId: record?.employee_details?.id,
                                employeeName: record?.employee_details?.employee_name,
                            },
                        });
                    };

                    return <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={navigateToAttendanceChart}>
                        <ChartLine className="h-4 w-4 text-sidebar-primary" />
                    </Button>;
                },
            },
            {
            accessor: "correction_history",
            Header: "History",
            align: "center",
            maxWidth: "90px",
            Cell: ({ row }) => {
                const record = row?.original ?? row;
                return (
                    <Button
                        variant="ghost"
                        size="sm"
                        className="h-8 w-8 p-0"
                        title="View correction history"
                        onClick={() => openCorrectionHistory(record?.employee_details)}
                    >
                        <History className="h-4 w-4 text-sidebar-primary" />
                    </Button>
                );
            },
        }
    ] : []),
        {
            accessor: "present",
            Header: "Present",
            sortable: true,
            align: "center",
            Cell: ({ value }) => (
                <Button
                    variant="outline"
                    className="text-green-600 border-green-200 bg-green-50 text-xs h-10"
                >
                    {value || 0}
                </Button>
            ),
        },
        {
            accessor: "absent",
            Header: "Absent",
            sortable: true,
            align: "center",
            Cell: ({ value }) => (
                <Button
                    variant="outline"
                    className="text-red-600 border-red-200 bg-red-50 text-xs h-10"
                >
                    {value || 0}
                </Button>
            ),
        },
        ...dynamicColumns,
    ], [dynamicColumns, isAdmin, isSuperAdmin, navigate]);

    // Check if we're currently viewing a specific month from payroll
    const isViewingSpecificMonth = !!payrollPeriod;

    return (
        <ContainerCard>
            <div className="flex justify-between items-center">
                <ModuleTitleCard
                    Icon={Calendar}
                    title='Attendance Log'
                    subtitle={isViewingSpecificMonth
                        ? `Viewing attendance for ${selectedMonth.format('MMMM YYYY')}`
                        : 'Manage daily attendance records'
                    }
                    totalRecords={totalRecords}
                />
                {isViewingSpecificMonth && (
                    <Button
                        onClick={handleClearLocationState}
                        variant="outline"
                        className="flex items-center gap-2"
                    >
                        Back to {dayjs().format('MMMM')} Month
                    </Button>
                )}
                {canReviewShiftChangeRequests() && (
                    <Button
                        variant="outline"
                        className="flex items-center gap-2"
                        onClick={() => navigate("/attendance/shift-change-requests")}
                    >
                        Shift Change Approvals
                    </Button>
                )}
            </div>
            <Reactable
                columns={AttendanceLogColumns}
                data={AttendanceLogData}
                setData={setAttendanceLogData}
                setTotalCount={setTotalRecords}
                setFetchFilter={setFetchFilter}
                url={MONTHWISE_ATTENDANCE_LOG}
                serverSide={true}
                serverTrigger={serverTrigger}
                rowSize="sm"
                isCSVRequired={false}
                isPDFRequired={false}
                isMonthYearRequired={true}
                selectedMonth={selectedMonth}
            />

            <DialogModal open={adminEditDialogOpen} onClose={handleAdminDialogHide} onSave={handleSave} saveLabel="Edit" loading={loading} title="Edit Attendance" width="72rem">
                <AttendanceEdit editDetails={selectedAttendanceData} ref={attendanceEditRef} onSuccess={() => setAdminEditDialogOpen(false)} />
            </DialogModal>

            <DialogModal
                open={historyDialogOpen}
                onClose={() => setHistoryDialogOpen(false)}
                onSave={() => setHistoryDialogOpen(false)}
                title={`Correction History — ${historyEmployeeName}`}
                saveLabel="Close"
                hideSaveButton={false}
                width="800px"
                showActions={true}
            >
                <AttendanceCorrectionHistory
                    employeeId={historyEmployeeId}
                    fromDate={historyFromDate}
                    toDate={historyToDate}
                />
            </DialogModal>

            <DialogModal open={attendanceRequestDialogOpen} onClose={handleRequestDialogHide} onSave={handleRequest} saveLabel="Request" title="Attendance Request" width="500px">
                <label className={modalFieldLabelClass}>Reason</label>
                <Textarea
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    placeholder="Enter a reason for Missing the attendance..."
                    rows={4}
                    showCharCount={true}
                    maxLength={250}
                    className={cn(
                        "w-full rounded-md transition-colors duration-200",
                        "border-gray-300 dark:border-gray-600",
                        "focus:border-sidebar-primary focus:ring-2 focus:ring-sidebar-primary/20",
                    )}
                />

                <p className="text-xs mt-2 italic text-gray-500 dark:text-gray-300">
                    Note: All correction requests will be reviewed and are subject to approval by the admin.
                </p>
            </DialogModal>

        </ContainerCard >
    );
}

export default AttendanceLog;